import React, { useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  Divider,
  IconButton,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Avatar,
  Menu,
  MenuItem,
  Chip,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  People as PeopleIcon,
  Assignment as TaskIcon,
  BarChart as ReportIcon,
  Settings as SettingsIcon,
  Logout as LogoutIcon,
  Person as PersonIcon,
  Business as BusinessIcon,
  AdminPanelSettings as AdminIcon,
  Api as ApiIcon,
  LinkOff as LinkOffIcon,
  CompareArrows as MergeIcon,
  Group as GroupIcon,
  History as HistoryIcon,
} from '@mui/icons-material';
import { useAuthStore } from '../../store/authStore';

const drawerWidth = 200;

const menuItems = [
  { text: 'Dashboard', icon: <DashboardIcon />, path: '/' },
  { text: 'Kunder', icon: <PeopleIcon />, path: '/customers' },
  { text: 'Opgaver', icon: <TaskIcon />, path: '/tasks' },
  { text: 'Rapporter', icon: <ReportIcon />, path: '/reports' },
  { text: 'Indstillinger', icon: <SettingsIcon />, path: '/settings' },
];

const adminMenuItems = [
  { text: 'Organisationer', icon: <BusinessIcon />, path: '/admin/organizations' },
  { text: 'Ansøgninger', icon: <AdminIcon />, path: '/admin/applications' },
  { text: 'Flowtale API', icon: <ApiIcon />, path: '/admin/flowtale-docs' },
  { text: 'Flowtale-log', icon: <HistoryIcon />, path: '/admin/flowtale-log' },
];

export const Layout: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuthStore();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const drawer = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ p: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Box
          component="img"
          src="/e3-icon.png"
          alt="E3 Spedition & Transport"
          sx={{ height: 36, width: 'auto', maxWidth: '100%', objectFit: 'contain' }}
        />
      </Box>
      <Divider />
      <List sx={{ py: 0.5, flex: 1 }}>
        {menuItems.map((item) => (
          <ListItem key={item.text} disablePadding sx={{ px: 0.5 }}>
            <ListItemButton
              selected={location.pathname === item.path}
              onClick={() => {
                navigate(item.path);
                setMobileOpen(false);
              }}
            >
              <ListItemIcon>{item.icon}</ListItemIcon>
              <ListItemText primary={item.text} />
            </ListItemButton>
          </ListItem>
        ))}
        {(user?.role === 'ADMIN' || user?.role === 'MANAGER') && (
          <>
            <ListItem disablePadding sx={{ px: 0.5 }}>
              <ListItemButton
                selected={location.pathname === '/unmapped-bookings'}
                onClick={() => {
                  navigate('/unmapped-bookings');
                  setMobileOpen(false);
                }}
              >
                <ListItemIcon><LinkOffIcon /></ListItemIcon>
                <ListItemText primary="Mapping" />
              </ListItemButton>
            </ListItem>
            <ListItem disablePadding sx={{ px: 0.5 }}>
              <ListItemButton
                selected={location.pathname === '/customer-merge'}
                onClick={() => {
                  navigate('/customer-merge');
                  setMobileOpen(false);
                }}
              >
                <ListItemIcon><MergeIcon /></ListItemIcon>
                <ListItemText primary="Duplikerede kunder" />
              </ListItemButton>
            </ListItem>
          </>
        )}
        {user?.role === 'ADMIN' && (
          <ListItem disablePadding sx={{ px: 0.5 }}>
            <ListItemButton
              selected={location.pathname === '/settings/users'}
              onClick={() => {
                navigate('/settings/users');
                setMobileOpen(false);
              }}
            >
              <ListItemIcon><GroupIcon /></ListItemIcon>
              <ListItemText primary="Brugere" />
            </ListItemButton>
          </ListItem>
        )}
      </List>
      {user?.isSuperAdmin && (
        <>
          <Divider />
          <List sx={{ py: 0.5 }}>
            <ListItem sx={{ py: 0.25 }}>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.625rem', letterSpacing: '0.05em' }}>
                SUPER ADMIN
              </Typography>
            </ListItem>
            {adminMenuItems.map((item) => (
              <ListItem key={item.text} disablePadding sx={{ px: 0.5 }}>
                <ListItemButton
                  selected={location.pathname === item.path}
                  onClick={() => {
                    navigate(item.path);
                    setMobileOpen(false);
                  }}
                >
                  <ListItemIcon>{item.icon}</ListItemIcon>
                  <ListItemText primary={item.text} />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </>
      )}
    </Box>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <AppBar
        position="fixed"
        sx={{
          top: 0,
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          ml: { sm: `${drawerWidth}px` },
          bgcolor: 'white',
          color: 'text.primary',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { sm: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          <Box sx={{ flexGrow: 1 }} />
          {user?.organization && (
            <Chip
              label={user.organization.name}
              size="small"
              sx={{ mr: 2 }}
              color={user.organization.status === 'TRIAL' ? 'warning' : 'default'}
            />
          )}
          <IconButton onClick={handleMenuOpen}>
            <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main' }}>
              {user?.firstName?.charAt(0)}
            </Avatar>
          </IconButton>
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
            onClick={handleMenuClose}
          >
            <MenuItem disabled>
              <PersonIcon sx={{ mr: 1 }} />
              {user?.firstName} {user?.lastName}
            </MenuItem>
            <MenuItem disabled>
              <Typography variant="caption" color="text.secondary">
                {user?.email}
              </Typography>
            </MenuItem>
            <Divider />
            <MenuItem onClick={handleLogout}>
              <LogoutIcon sx={{ mr: 1 }} />
              Log ud
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>
      <Box
        component="nav"
        sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth, top: 0, height: '100%' },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth, top: 0, height: '100%' },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 2,
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          mt: '48px',
          minHeight: 'calc(100vh - 48px)',
          bgcolor: 'background.default',
        }}
      >
        <Outlet />
      </Box>
    </Box>
  );
};
